import Vue from 'vue'
import ExpressionResolver from './helpers/expression-resolver-store'
import * as types from './mutation-types'
import { cloneDeep, first, get, isEmpty, isUndefined, isEqual, isNil } from 'lodash'
import { convertToBool, dropdownHasValue, hasValue, isSubFormPath, needToSkip, parsePath } from '../../../utils'
import { highlightChange } from './helpers/notifications'
import {
  createForm,
  getPropToChange,
  isSqlSelect,
  isSubForm,
  prepareExpression,
  prepareQuery,
  refreshList,
} from '../../../form'
import { getFilter, prepareValue } from '../../../utils/prepare-value'
import {
  EXPRESSION_TYPES,
  FIELD_PROPS,
  FIELD_REQUIRED_LEVELS,
  FIELD_TYPES,
  FORMATTED_FIELD_TYPES,
  MAIN_FORM_NAME,
  SUBMIT_TYPES,
  BOOLEAN_FIELD_PROPERTIES,
} from '../../../consts'
import { castValueTypeToMatchKeyType } from '../../../form'
import { SOURCE_TYPES } from '../../../components/list-new/lib/list-constants'
import { getFormattedDropdownValue, getSqlChosenFormattedValue } from '../list-new/helpers'
import EventBus from '../../../utils/event-bus'
import { SUB_FORM_PROPS } from './helpers/constants'

function getFirstPageOfOptions(options = [], fieldValue) {
  if (dropdownHasValue(fieldValue)) {
    const isArray = Array.isArray(fieldValue)
    const valueArray = isArray ? fieldValue : [fieldValue]
    valueArray.forEach((key) => {
      const index = options.findIndex((option) => String(option.key) === String(key))
      if (index >= 0) {
        options.unshift(first(options.splice(index, 1)))
      }
    })
  }
  const optionRenderLimit = get(window, 'CORE.env.optionRenderLimit', 50)
  const completed = options.length <= optionRenderLimit
  return { options: options.slice(0, optionRenderLimit), completed }
}

const TIME = 'time'
const DATE = 'date'
const DATE_TIME = 'datetime-local'
const DATE_FORMAT = 'YYYY-MM-DD'
const DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm:ss'

const DEFAULT_EXPRESSION_TYPES = [
  'defaultValue',
  'hiddenExp',
  'filterExp',
  'listExp',
  'readOnly',
  'readOnlyExp',
  'requiredExp',
  'labelExp',
]

function convertDateToString(date, format) {
  return moment(new Date(date)).format(format)
}

function convertDateValue(type, propToChange, value) {
  if (propToChange !== FIELD_PROPS.VALUE) return value
  switch (type) {
    case DATE_TIME:
      value = value ? convertDateToString(value, DATE_TIME_FORMAT) : null
      break
    case DATE:
      value = value ? convertDateToString(value, DATE_FORMAT) : null
      break
  }
  return value
}

function convertDropdownValue(type, list, value, isMultiselect) {
  if (type === FIELD_TYPES.DROPDOWN && !isEmpty(get(list, 'options', [])) && dropdownHasValue(value)) {
    const castValue = castValueTypeToMatchKeyType(first(list.options), value)
    return isMultiselect ? castValue : first(castValue)
  }
  return value
}

/**
 * save or validate the form data
 * @param {Function} config.commit - vuex commit function
 * @param {Object} config.state - vuex state
 * @param {string} config.mode - "save" or "verification"
 */
function saveOrVerificationData({ commit, state, mode, getters }) {
  const isVerification = mode === 'verification'

  let idMainTable = state.idMainTable
  if (state.isCloning) {
    idMainTable = null
  }

  const headers = generateHeaders(state)
  const formData = isVerification
    ? getters.getFormData()
    : Object.assign({}, getters.getFormData(), { triggerNames: state.triggerNames })

  const parameters = {
    idForm: state.idForm,
    idMaintable: idMainTable,
    [isVerification ? 'data' : 'formData']: JSON.stringify(formData),
    externalParams: JSON.stringify(state.externalParams),
    transaction: null,
    controllerName: get(window.CORE, 'requestController', ''),
    path: get(window.CORE, 'requestRoute', ''),
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  }

  return Vue.prototype.$client
    .execute({
      headers,
      operationId: isVerification ? 'Dynform_formVerification' : 'Dynform_saveData',
      method: 'post',
      parameters,
      requestContentType: 'application/x-www-form-urlencoded',
    })
    .catch((e) => console.error(e.message))
    .then((response) => {
      commit(isVerification ? types.FORM_VERIFICATION_DATA : types.FORM_SAVE_DATA, { data: response.body })
      return response.body
    })
}

/**
 * @description headerek eloallitasa
 * @param state
 * @returns {{}}
 */
function generateHeaders(state) {
  let headers = {}
  if (state.inPopup && !isEmpty(state.popupUrl)) {
    headers['Routed-Url'] = state.popupUrl
  }
  return headers
}

/**
 * @description Kapot erteket tombben ad vissza, amennyiben az nem tomb
 * @param value
 * @returns {*|*[]}
 */
function getValueArray(value) {
  return Array.isArray(value) ? value : [value]
}

/**
 * @description Visszaadja a dropdown erteket tombben, ertek nelkul null-t
 * @param value
 * @returns {[*]|null}
 */
function getSelectionValue(value) {
  return dropdownHasValue(value) ? getValueArray(value) : null
}

/**
 * @description filter expression futtatasa
 * @param state
 * @param getters
 * @param dispatch
 * @param expression
 * @param sfRowIndex
 * @param sfId
 * @param path
 * @param fieldGetter
 * @param fieldPathMap
 * @param field
 * @returns {Promise<void>}
 */
async function commitFilter({
  state,
  dispatch,
  expression,
  sfRowIndex,
  path,
  fieldGetter,
  fieldPathMap,
  field,
  formName,
}) {
  if (isSqlSelect(field)) {
    const filter = await getFilter({
      expression,
      sfRowIndex,
      fieldGetter,
      fieldPathMap,
      namespace: state.storeNamespace,
    })
    let order = []
    if (filter?.order?.length) {
      order = filter.order.map((elem) => {
        const [column, dir] = elem.split(' ')
        return { column, dir }
      })
      dispatch('updatePropByPath', {
        path,
        prop: 'orderExp',
        value: order,
        formName,
      })
    }
    dispatch('updatePropByPath', {
      path,
      prop: 'searchExp',
      value: filter,
      byExpression: true,
      formName,
    })
  }
}

async function resolveListWithLazyLoad({ idForm, fieldId, fieldValue, contextData, externalParams }) {
  const selection = dropdownHasValue(fieldValue) ? (Array.isArray(fieldValue) ? fieldValue : [fieldValue]) : null
  const parameters = {
    sourceType: SOURCE_TYPES.FORM,
    idSource: idForm,
    fieldId,
    contextData,
    externalParams,
    selection,
    page: 0,
  }
  const { obj } = await Vue.prototype.$client.execute({
    operationId: 'LazyLoader_bySourceType',
    parameters,
  })
  return obj
}

function getRequiredLevel({ value, getters, path }) {
  if (!value) return FIELD_REQUIRED_LEVELS.NONE
  const fieldGetter = getters.expressionFieldByPath
  const field = fieldGetter(path)
  return get(field, [FIELD_PROPS.FORMATS, FIELD_PROPS.REQUIRED], FIELD_REQUIRED_LEVELS.ERROR)
}

async function updateField({ getters, dispatch, change, rowId, value, needDebounce, formName }) {
  const path = change.path.replace('rowId', rowId)
  const prop = change.propToChange
  if (prop === FIELD_PROPS.REQUIRED) {
    value = getRequiredLevel({ value, getters, path })
  }
  if (isUndefined(value)) return
  if (BOOLEAN_FIELD_PROPERTIES.includes(prop)) {
    value = !!value
  }
  await dispatch('updatePropByPath', {
    path,
    prop,
    value,
    byExpression: true,
    needDebounce,
    formName,
  })
}

export async function resolveAndChangeValue({
  state,
  getters,
  dispatch,
  change,
  sfRowIndex,
  expression,
  triggerName,
  formName,
}) {
  let value
  const fieldPath = change.path.replace('rowId', sfRowIndex)
  const fieldState = getters.expressionFieldByPath(fieldPath)
  const { id: fieldId, value: fieldValue, type, list, multiselect } = fieldState
  if (change.propToChange === FIELD_PROPS.LIST) {
    if (fieldState.type === FIELD_TYPES.SQL_CHOSEN) {
      dispatch('refreshSqlChosenByPath', { path: fieldPath, fieldState, formName })
      return
    }
    const idForm = getters.idFormByFieldPath(fieldPath)
    const contextData = getters.getListExpressionContextData(fieldPath)
    const externalParams = JSON.stringify(getters.externalParams)
    value = await resolveListWithLazyLoad({
      idForm,
      fieldId,
      fieldValue,
      contextData,
      externalParams,
    })
  } else {
    value = await ExpressionResolver.resolve({
      fieldState,
      expression,
      sfRowIndex,
      namespace: state.storeNamespace,
      triggerName,
    })
    value = convertDateValue(type, change.propToChange, value)
    value = convertDropdownValue(type, list, value, multiselect)
  }
  const needDebounce = change.needDebounce || false
  await updateField({
    getters,
    dispatch,
    change,
    rowId: sfRowIndex,
    value,
    needDebounce,
    formName,
  })
}

function parseFieldPath(path) {
  const parsedPath = {
    fieldPath: 'form',
  }

  if (path === parsedPath.fieldPath) return parsedPath

  let [pageId, groupId, rowId, fieldId, subFormRowId, colId] = parsePath(path)

  if (pageId) parsedPath.fieldPath += `.pages[${pageId}]`
  if (groupId) parsedPath.fieldPath += `.groups[${groupId}]`
  if (rowId && fieldId) parsedPath.fieldPath += `.rows[${rowId}][${fieldId}]`
  if (subFormRowId && colId) {
    parsedPath.subFormPath = `${parsedPath.fieldPath}`
    parsedPath.fieldPath += `.subForm.rows[${subFormRowId}].items[${colId}]`
    parsedPath.rowId = subFormRowId
  }

  return parsedPath
}

export default {
  resolveFilter({ commit, state, dispatch }, { expression, sfRowIndex, sfId, path, formName }) {
    const fieldGetter = this.getters[`${state.storeNamespace}/expressionFieldByPath`]
    const fieldPathMap = get(state, 'fieldPathMap', {})
    const field = fieldGetter(path)
    expression = prepareExpression(prepareQuery(expression))
    commitFilter({
      state,
      dispatch,
      expression,
      sfRowIndex,
      sfId,
      path,
      fieldGetter,
      fieldPathMap,
      field,
      formName,
    }).catch((error) => console.error(`Commit filter error: ${error.message}`))
  },
  update({ commit, state }, key, value) {
    commit(types.FIELD_UPDATE_VALUE, { key, value })
  },

  async fetchStructure({ commit, state }) {
    const parameters = {
      idForm: state.idForm,
      idMaintable: state.idMainTable,
      path: get(window.CORE, 'requestRoute', ''),
    }

    const response = await Vue.prototype.$client
      .execute({
        operationId: 'Dynform_getProperties',
        parameters,
      })
      .catch((e) => console.error(e.message))
    commit(types.FORM_FETCH_STRUCTURE, response.body)
  },

  async fetchData({ commit, state }) {
    const headers = generateHeaders(state)

    const parameters = {
      idForm: state.idForm,
      idMaintable: state.idMainTable,
      externalParams: JSON.stringify(state.externalParams),
      path: get(window.CORE, 'requestRoute', ''),
    }

    await Vue.prototype.$client
      .execute({
        headers,
        operationId: 'Dynform_getData',
        parameters,
      })
      .catch((e) => console.error('Dynform_getData', e.message))
      .then((response) => {
        if (response.body.error) {
          throw new Error(response.body.error.message)
        }
        commit(types.FORM_FETCH_DATA, response.body)
      })
  },

  formRefresh({ commit, state }) {
    const parameters = {
      idForm: state.idForm,
      idMaintable: state.idMainTable,
    }

    Vue.prototype.$client
      .execute({
        operationId: 'Dynform_refreshData',
        parameters,
      })
      .catch((e) => console.error(e.message))
      .then((response) => {
        const tmpValues = cloneDeep(state.formData)
        const data = Object.assign({}, tmpValues, response.body)
        const form = refreshList(cloneDeep(state.form), data)
        commit(types.FORM_REFRESH, { form })
      })
  },

  saveData({ commit, state, getters }) {
    const data = cloneDeep(state)
    if (state.isCloning) {
      data.idMainTable = null
    }

    return saveOrVerificationData({
      commit,
      state: data,
      getters,
      mode: 'save',
    })
  },

  formVerification({ commit, state, getters }) {
    return saveOrVerificationData({
      commit,
      state,
      getters,
      mode: 'verification',
    })
  },

  removeErrorMessage({ commit, state }, error) {
    commit(types.FORM_REMOVE_ERROR_MESSAGE, error)
  },

  lockSet({ commit, state }) {
    const parameters = {
      formID: state.idForm,
      idMaintable: state.idMainTable,
      lockMode: 'LOCK',
      lockKey: state.lockKey,
      trans: null,
    }

    Vue.prototype.$client
      .execute({
        operationId: 'Lock_lockForm',
        method: 'get',
        parameters,
        requestContentType: 'application/x-www-form-urlencoded',
      })
      .catch((e) => console.error(e.message))
      .then((response) => {
        commit(types.FORM_LOCK_INFO, { data: response.body })
      })
  },

  load({ commit, dispatch, state, rootState }) {
    Promise.all([dispatch('fetchStructure'), dispatch('fetchData')]).then(() => {
      const values = Object.values(state.formStructure.forms)
      const data = state.formData
      const form = createForm(values, data)
      commit(types.FORM_LOAD, { rootState, formData: form })
    })
  },

  async resolveSubFormExpressions(
    { dispatch, state },
    { subForm = false, formId = null, rowId = null, path, formName }
  ) {
    const subFormExpressions = this.getters[`${state.storeNamespace}/subFormExpressions`]
    if (!subFormExpressions) return
    for (const expressionType of DEFAULT_EXPRESSION_TYPES) {
      if (subFormExpressions.hasOwnProperty(expressionType)) {
        for (let fieldPath of Object.keys(subFormExpressions[expressionType])) {
          const subFormPath = fieldPath.split('/').slice(0, 4).join('/')

          if (path !== subFormPath) continue

          if (subForm && subFormPath !== fieldPath && isNil(rowId)) {
            const fieldGetter = this.getters[`${state.storeNamespace}/expressionFieldByPath`]
            const subFormRows = get(fieldGetter(subFormPath), ['subForm', 'rows'], [])

            for (const rowIndex in subFormRows) {
              const propToChange = getPropToChange(expressionType)
              const expression = cloneDeep(subFormExpressions[expressionType][fieldPath][propToChange])
              expression.path = expression.path.replace('rowId', rowIndex)
              expression.type = expressionType
              dispatch('resolveSubFormExpression', { ...expression, formId, rowId: rowIndex, formName })
            }
          } else {
            const propToChange = getPropToChange(expressionType)
            const expression = cloneDeep(subFormExpressions[expressionType][fieldPath][propToChange])
            expression.path = expression.path.replace('rowId', rowId)
            expression.type = expressionType
            await dispatch('resolveSubFormExpression', { ...expression, formId, rowId, formName })
          }
        }
      }
    }
  },

  async resolveSubFormExpression(
    { commit, dispatch, state, getters },
    { expression, type, path, formId, rowId = null, propToChange, formName }
  ) {
    const fieldGetter = this.getters[`${state.storeNamespace}/expressionFieldByPath`]
    const field = fieldGetter(path)

    if (!field) return

    if ([EXPRESSION_TYPES.FILTER, EXPRESSION_TYPES.LIST].includes(type)) {
      return
      // @TODO:dropdown csekkolni, hogy minden dd faszan szedi-e maganak a hianyzo ertekeket
      // const fieldPathMap = get(state, 'fieldPathMap', {})
      // await commitFilter({
      //   state,
      //   getters: this.getters,
      //   dispatch,
      //   expression,
      //   sfRowIndex: rowId,
      //   sfId: formId,
      //   path,
      //   fieldGetter,
      //   fieldPathMap,
      //   field,
      // })
      // return
    }

    let value = await ExpressionResolver.resolve({
      fieldState: field,
      expression,
      sfRowIndex: rowId,
      namespace: state.storeNamespace,
    })

    switch (propToChange) {
      case 'hidden':
      case 'readOnly': {
        value = convertToBool(value)
        break
      }
      case 'label': {
        value = `${value}`
        break
      }
      case 'required': {
        propToChange = FIELD_PROPS.VALIDATIONS
        const errorLevel = get(field, [FIELD_PROPS.FORMATS, FIELD_PROPS.REQUIRED], FIELD_REQUIRED_LEVELS.ERROR)
        value = Object.assign({}, field.validations, {
          required: value ? errorLevel : FIELD_REQUIRED_LEVELS.NONE,
        })
        break
      }
      case 'list': {
        const fieldValue = get(field, 'value')
        value = getFirstPageOfOptions(value, fieldValue)
        break
      }
      case 'value': {
        value = prepareValue(field, value)
        if (FORMATTED_FIELD_TYPES.includes(field.type)) {
          const contextData = getters.getListExpressionContextData(path)
          const externalParams = JSON.stringify(getters.externalParams)
          const listValue = await resolveListWithLazyLoad({
            idForm: formId,
            fieldId: field.id,
            fieldValue: value,
            contextData,
            externalParams,
          })

          dispatch('updatePropByPath', {
            path,
            prop: propToChange,
            value: listValue,
            byExpression: true,
            formName,
          })
        }
        propToChange = isSubForm(field) ? 'subformValue' : propToChange
        break
      }
    }

    dispatch('showExpNotification', { rowId, field, prop: propToChange, value })
    dispatch('updatePropByPath', {
      path,
      prop: propToChange,
      value,
      byExpression: true,
      formName,
    })
  },

  /**
   * @description ertesites megjelenitese mezo szamolasok eseten
   * @param state
   * @param rowId
   * @param field
   * @param prop
   * @param value
   */
  showExpNotification({ state }, { rowId, field, prop, value }) {
    if (!get(state.showNotiOnExp, `${prop}`, false)) return
    const propMap = ['hidden', 'value']
    if (propMap.includes(prop) && field[prop] !== value) {
      // subFormon eltart egy darabig, mig a DOMba bekerulnek az elemek
      setTimeout(() => {
        highlightChange(rowId, field.name, field.label, prop)
      }, 500)
    }
  },

  setCaptchaText({ commit }, { captchaText }) {
    commit(types.SET_CAPTCHA_TEXT, { captchaText })
  },
  setCaptchaValue({ commit }, { captchaValue }) {
    commit(types.SET_CAPTCHA_VALUE, { captchaValue })
  },
  setCaptchaFieldPath({ commit }, { captchaPath }) {
    commit(types.SET_CAPTCHA_FIELD_PATH, { captchaPath })
  },
  setFormCaptcha({ commit }, { formCaptcha }) {
    commit(types.SET_FORM_CAPTCHA, { formCaptcha })
  },
  addSubFormRow({ commit }, { path, row }) {
    commit(types.ADD_SUB_FORM_ROW, { path, row })
  },
  removeSubFormRow({ commit }, { path, rowIndex }) {
    commit(types.REMOVE_SUB_FORM_ROW, { path, rowIndex })
  },
  setSubFormRowData({ commit }, { path, rowIndex, data }) {
    commit(types.FORM_UPDATE_SUBFORM_ROW_BY_PATH, { path, rowIndex, data })
  },
  setRowCollapsed({ commit }, { path, collapsed }) {
    commit(types.SET_SUB_FORM_ROW_COLLAPSED, { path, collapsed })
  },
  async resolveNumberFormats({ state, getters, dispatch, commit }, { field, path }) {
    if (field.formats.min) {
      const change = {
        propToChange: 'minValue',
        path,
      }
      await resolveAndChangeValue({
        state,
        getters,
        dispatch,
        change,
        expression: field.formats.min,
      })
    }
    if (field.formats.max) {
      const change = {
        propToChange: 'maxValue',
        path,
      }
      await resolveAndChangeValue({
        state,
        getters,
        dispatch,
        change,
        expression: field.formats.max,
      })
    }
  },

  /**
   * @description Update prop's value by path
   * @param state
   * @param commit
   * @param dispatch
   * @param getters
   * @param path
   * @param prop
   * @param value
   * @param formName
   * @param options
   * @param byExpression
   * @param needDebounce
   */
  async updatePropByPath(
    { state, commit, dispatch, getters },
    { path, prop = 'value', value, formName = MAIN_FORM_NAME, options, byExpression = false, needDebounce = false }
  ) {
    const { fieldPath, subFormPath, rowId } = parseFieldPath(path)
    const field = get(state, fieldPath)

    if (!field.validations) {
      commit(types.UPDATE_PROP_BY_PATH, {
        fieldPath,
        prop: FIELD_PROPS.VALIDATIONS,
        value: {},
        byExpression: true,
      })
    }

    const valueChanged =
      prop === FIELD_PROPS.REQUIRED ? !isEqual(field.validations[prop], value) : !isEqual(field[prop], value)

    if (field && valueChanged) {
      if (field.type === FIELD_TYPES.NUMBER && prop === 'value') {
        await dispatch('resolveNumberFormats', { field, path })
        if (field.minValue && value < field.minValue) value = field.minValue
        if (field.maxValue && value > field.maxValue) value = field.maxValue
      }
      value = convertDateValue(field.type, prop, value)

      commit(types.UPDATE_PROP_BY_PATH, {
        fieldPath,
        prop,
        value,
        byExpression,
        needDebounce,
      })

      if (FORMATTED_FIELD_TYPES.includes(field.type) && prop === FIELD_PROPS.VALUE) {
        let formattedValue = ''
        options = get(field, 'dynamicOptions', options)
        if (hasValue(value)) {
          switch (field.type) {
            case FIELD_TYPES.DROPDOWN:
            case FIELD_TYPES.MULTISELECT:
            case FIELD_TYPES.RADIO_BUTTON_LIST:
              const idForm = subFormPath ? get(state, `${subFormPath}.${SUB_FORM_PROPS.SUB_ID_FORM}`) : getters.idForm
              const contextData = getters.getListExpressionContextData(path)
              const fieldId = field.id
              formattedValue = await getFormattedDropdownValue({ options, value, idForm, contextData, fieldId })
              break
            case FIELD_TYPES.SQL_CHOSEN:
              formattedValue = getSqlChosenFormattedValue(field.listData || [], field.relField || '', value)
              break
            default:
              break
          }
        }
        commit(types.UPDATE_PROP_BY_PATH, {
          fieldPath,
          prop: FIELD_PROPS.FORMATTED_VALUE,
          value: formattedValue,
          byExpression,
          needDebounce,
        })
      }
      if (byExpression) return
      if (field.valueIsDefault && prop === FIELD_PROPS.VALUE) {
        commit(types.SET_FIELD_TO_SKIP, { fieldName: field.name })
      }
      dispatch('triggerExpressionResolver', { field, prop, formName, rowId })
    }
  },

  async triggerExpressionResolver({ state, commit, getters, dispatch }, { field, prop, formName, rowId }) {
    const fieldName = field.name
    const chain = get(state, ['expressionChain', formName, fieldName], [])

    const isCreate = getters.submitRoot === SUBMIT_TYPES.CREATE
    const fieldsToSkip = get(state, 'fieldsToSkip', [])
    const fieldByPath = getters.expressionFieldByPath

    for (const change of chain) {
      if (needToSkip({ isCreate, change, prop, fieldsToSkip })) continue
      const field = fieldByPath(change.path)
      commit(types.SET_TRIGGER_NAME, { fieldId: field.id, triggerName: fieldName })

      let expression = change.expression
      const needIterationOnSubFormRows = isSubFormPath(change.path) && isUndefined(rowId)
      if (needIterationOnSubFormRows) {
        // foform triggerel subFormvaltozast
        const subFormRows = get(field, 'subForm.rows', [])
        for (const sfRowIndex in subFormRows) {
          await resolveAndChangeValue({
            state,
            getters,
            dispatch,
            change,
            sfRowIndex,
            expression,
            triggerName: fieldName,
            formName,
          })
        }
      } else {
        if (!isSubFormPath(change.path)) {
          // subForm triggerrel foformvaltozast, vagy foform foformot
          await resolveAndChangeValue({
            state,
            getters,
            dispatch,
            change,
            sfRowIndex: null,
            expression,
            triggerName: fieldName,
            formName,
          })
        } else {
          // subForm triggerel subFormot, vagy foform foformot
          await resolveAndChangeValue({
            state,
            getters,
            dispatch,
            change,
            sfRowIndex: rowId,
            expression,
            triggerName: fieldName,
            formName,
          })
        }
      }
    }

    if (formName !== MAIN_FORM_NAME) {
      const subFormPath = get(state, ['fieldPathMap', fieldName])
      const parentField = getters.expressionFieldByPath(subFormPath)
      dispatch('triggerExpressionResolver', { field: parentField, prop: FIELD_PROPS.VALUE, formName: MAIN_FORM_NAME })
    }
  },

  formLoad({ commit, dispatch, rootState }, { formData }) {
    commit(types.FORM_LOAD, { rootState, formData })
  },

  setRowUid({ commit, state }, { index, path }) {
    commit(types.SET_ROW_UID, { index, path })
  },

  setActivePageId({ commit, state }, { activePageId }) {
    commit(types.SET_ACTIVE_PAGE_ID, { activePageId })
  },

  refreshSqlChosenByPath({ state, dispatch, commit }, { path, fieldState, formName }) {
    const relFormId = get(fieldState, 'relFormId')
    const id = `${relFormId}-${get(fieldState, 'name')}`
    const filterExp = get(fieldState, 'filterExp.exp', {})
    dispatch('resolveFilter', { expression: filterExp, path, formName })
    EventBus.$emit(`apply-filter-${id}`, { idForm: relFormId })
  },
}
